import type {
  ExternalRecordCapabilities,
  ExternalRecordRequest,
  ExternalRecordResponse,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Out-of-band transitions: what a PSP did that DeloPay never observed.
 *
 * A refund issued in the PSP's own dashboard, a chargeback settled by phone, a
 * payment the rail holds as paid but never sent a usable notification for. The
 * transition is written through the normal path, an outgoing webhook is raised
 * for the shop, and an audit row records who asserted it and why.
 *
 * This asserts a fact about somebody else's money, so it is deliberately
 * expensive to call: the reason is compelled, the PSP's own reference belongs
 * with it, and every record is attributable. It needs a dashboard session with
 * the profile external-record write permission — an API key reaches nothing
 * here.
 */
export class ExternalRecords {
  constructor(private readonly request: RequestFn) {}

  /**
   * Record one out-of-band transition.
   * `POST /external-records`
   *
   * Which kinds a deployment answers for depends on the payment's connector
   * and on which arms are live: an arm that is not served answers `501`, which
   * is a statement about this rail rather than about the request.
   *
   * @param params - What happened, and why it is being recorded.
   */
  async create(params: ExternalRecordRequest): Promise<ExternalRecordResponse> {
    return this.request('POST', '/external-records', { body: params });
  }

  /**
   * What this deployment will accept, per kind.
   * `GET /external-records/capabilities`
   *
   * Read it before offering the operator a form. Every kind is listed, so an
   * entry that is not served is distinguishable from a kind this release does
   * not know at all — and `create` says *why* it is not served: a gap
   * (`not_implemented`) or a rule (`not_applicable`). Treating those as the
   * same thing is how a caller ends up waiting for something that will never
   * be built.
   *
   * The answer describes the binary rather than anything a merchant owns, so
   * it is the same for every caller of one deployment and is safe to cache for
   * the life of a dashboard session.
   */
  async capabilities(): Promise<ExternalRecordCapabilities> {
    return this.request('GET', '/external-records/capabilities');
  }
}
